export const formatDate = (dateString) => {
  const date = new Date(dateString);
  // Format date as needed
  return date.toLocaleDateString();
};

export const capitalize = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const formatArea = (area) => {
  if(!area || area == -1){
    return {value: null, unit: null};
  }
  if (area >= 1000000) {
    // If area is greater than or equal to 1,000,000 square meters, display in square kilometers
    return {value: (area / 1000000).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " "), unit: "km²"};
  } else if (area >= 10000) {
    // If area is greater than or equal to 10,000 square meters, display in hectares
    return {value: (area / 10000).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " "), unit: "ha"};
  } else {
    // Display in square meters
    return {value: area.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " "), unit: "m²"};
  }
};

/**
 * Converts a timestamp in seconds or nanoseconds to a Date object or formatted date string.
 * @param {number|string} timestamp - The timestamp in seconds or nanoseconds.
 * @param {object} [options] - Optional parameters.
 * @param {boolean} [options.isNanoseconds=false] - Set to true if the timestamp is in nanoseconds.
 * @param {boolean} [options.toString=false] - Set to true to return a formatted date string.
 * @param {string} [options.locale='en-US'] - Locale for date formatting.
 * @param {object} [options.formatOptions] - Options for date formatting.
 * @returns {Date|string} - The Date object or formatted date string.
 */

export function convertTimestamp(timestamp, options = {}) {
  const {
      isNanoseconds = false,
      toString = false,
      locale = 'en-US',
      formatOptions = {},
      customFormat = false,
  } = options;

  // Ensure the timestamp is a number
  let ts = Number(timestamp);

  // If the timestamp is in nanoseconds, convert to milliseconds
  if (isNanoseconds) {
      ts = Math.floor(ts / 1e6);
  } else {
      // If the timestamp is in seconds, convert to milliseconds
      ts = ts * 1000;
  }

  // Create a Date object
  const date = new Date(ts);

  // Check for invalid date
  if (isNaN(date.getTime())) {
      throw new Error('Invalid timestamp provided.');
  }

  // Return the Date object or formatted date string
  if (!toString) {
    return date; // just return the Date object
  }

  // If the user wants the custom "HH:MM dd/MM/yyyy" format
  if (customFormat) {
    // Build it manually
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Months are zero-indexed in JS
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    // "HH:MM dd/MM/yyyy"
    return `${hours}:${minutes} ${day}/${month}/${year}`;
  }

  // Otherwise, default to toLocaleString
  return date.toLocaleString(locale, formatOptions);
}

/**
 * Parse a JSON string.
 *
 * This function attempts to parse the provided JSON string. If the parsing
 * fails (e.g., due to invalid JSON), it catches the error and returns null.
 *
 * @param {string} jsonString - The JSON string to parse.
 * @returns {any|null} - The parsed object if valid JSON, or null if parsing fails.
 */
export function parseJSON(jsonString) {
  if (!jsonString) return null;
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    return null;
  }
}

/**
 * Sorts an array of result objects by their "name" property.
 * Each name is expected to follow the format "Result X", where X is a number.
 *
 * @param {Array<Object>} results - The array of objects to be sorted. Each object should have a "name" property.
 * @returns {Array<Object>} The sorted array, in ascending order based on the numeric value in the name.
 */
export function sortResultsByName(results) {
  return results.sort((a, b) => {
    const nameA = a.name || "";
    const nameB = b.name || "";
    // Use a regular expression to extract the number from names like "Result 1", "Result 2", etc.
    const regex = /Result\s*(\d+)/i;
    const matchA = nameA.match(regex);
    const matchB = nameB.match(regex);

    if (matchA && matchB) {
      const numA = parseInt(matchA[1], 10);
      const numB = parseInt(matchB[1], 10);
      return numA - numB;
    } else {
      // If no numeric part can be extracted, fall back to lexicographical comparison.
      return nameA.localeCompare(nameB);
    }
  });
}


/**
 * Formats a cost value.
 *
 * If the cost is 1,000,000 or more, the function converts it to millions
 * and appends " mill" (e.g., 10,000,000 becomes "10 mill"). If the millions value
 * is not a whole number, it will be rounded to one decimal place.
 *
 * For costs below 1,000,000, it returns a localized string.
 *
 * @param {number} cost - The numeric cost value.
 * @returns {object} - The formatted cost string.
 *
 * @example
 * formatCost(10000000)    // "10 mill"
 * formatCost(12500000)    // "12.5 mill"
 * formatCost(500000)      // "500 000" (or locale formatted)
 */
export function formatCost(cost) {
  if (!cost || cost == -1 || typeof cost !== 'number') return {value: null, unit: null};

  /* if (cost >= 1e6) {
    const millions = cost / 1e6;
    // Use no decimals if the millions value is a whole number, else 1 decimal
    const formattedMillions = Number.isInteger(millions) ? millions : millions.toFixed(1);
    return  {value: `${formattedMillions}M`, unit: "€"};
  } */
  
  return {value: cost.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " "), unit: '€'};
}

